/**
 * Email Testing Utility
 * 
 * This utility provides easy-to-use functions for testing email functionality
 * from the browser console or React components.
 */

import { SendEmail } from '@/api/integrations';
import { supabase } from '@/lib/supabase';
import { getCurrentUser } from '@/lib/supabase';

/**
 * Verify user is authenticated before running tests
 * @returns {Promise<boolean>} True if authenticated
 */
async function verifyAuthentication() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Session error:', error);
      return false;
    }
    
    if (!session) {
      console.error('No session found');
      return false;
    }
    
    if (!session.access_token) {
      console.error('No access token in session');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Authentication verification failed:', error);
    return false;
  }
}

/**
 * Quick test for Edge Function configuration
 * @param {string} testEmail - Email address to send test email to
 * @returns {Promise<Object>} Test result
 */
export async function testEdgeFunction(testEmail) {
  console.log('🧪 Testing Edge Function Configuration...');
  
  // Verify authentication first
  const isAuthenticated = await verifyAuthentication();
  if (!isAuthenticated) {
    const error = 'Auth session missing! Please log in to the app first.';
    console.log('❌ Edge Function Test: FAILED');
    console.log('   Error:', error);
    return { 
      success: false, 
      error,
      diagnostic: 'You must be logged in to test email functionality. Please refresh the page and log in, then try again.'
    };
  }
  
  try {
    const response = await SendEmail({
      to: testEmail,
      subject: 'Test Email from Summer Squad',
      template: 'welcome',
      data: {
        userName: 'Test User',
        userEmail: testEmail,
        discoverUrl: window.location.origin + '/discover'
      }
    });
    
    if (response && response.success) {
      console.log('✅ Edge Function Test: PASSED');
      console.log('   Message ID:', response.messageId);
      return { success: true, messageId: response.messageId };
    } else {
      console.log('❌ Edge Function Test: FAILED - No success response');
      console.log('   Response:', response);
      return { success: false, error: 'No success response', response };
    }
  } catch (error) {
    console.log('❌ Edge Function Test: FAILED');
    console.log('   Error:', error.message);
    console.log('   Full error:', error);
    
    let diagnostic = '';
    if (error.message.includes('RESEND_API_KEY')) {
      diagnostic = 'RESEND_API_KEY is not configured in Supabase Edge Function secrets';
    } else if (error.message.includes('Unauthorized')) {
      diagnostic = 'Authentication failed - make sure you\'re logged in';
    } else if (error.message.includes('non-2xx') || error.message.includes('Edge Function')) {
      diagnostic = 'Edge Function may not be deployed, returned an error, or is not accessible. Check Supabase Edge Function logs for details.';
    } else if (error.message.includes('template')) {
      diagnostic = 'Email template may have an issue. Check Edge Function logs.';
    }
    
    if (diagnostic) {
      console.log('   ⚠️  Diagnostic:', diagnostic);
    }
    
    return { success: false, error: error.message, diagnostic };
  }
}

/**
 * Test friend invitation email
 * @param {string} testEmail - Email address to send test email to
 * @returns {Promise<Object>} Test result
 */
export async function testFriendInvitationEmail(testEmail) {
  console.log('📧 Testing Friend Invitation Email...');
  
  // Verify authentication first
  const isAuthenticated = await verifyAuthentication();
  if (!isAuthenticated) {
    const error = 'Auth session missing! Please log in to the app first.';
    console.log('❌ Friend Invitation Test: FAILED');
    console.log('   Error:', error);
    return { 
      success: false, 
      error,
      diagnostic: 'You must be logged in to test email functionality.'
    };
  }
  
  try {
    const response = await SendEmail({
      to: testEmail,
      template: 'friend-invitation',
      data: {
        senderName: 'Test Sender',
        senderEmail: 'sender@example.com',
        recipientName: 'Test Recipient',
        message: 'This is a test friend invitation',
        inviteUrl: window.location.origin + '/auth/signup?invite=test-123',
        appUrl: window.location.origin
      }
    });
    
    if (response && response.success) {
      console.log('✅ Friend Invitation Test: PASSED');
      console.log('   Message ID:', response.messageId);
      return { success: true, messageId: response.messageId };
    } else {
      console.log('❌ Friend Invitation Test: FAILED');
      return { success: false, error: 'No success response', response };
    }
  } catch (error) {
    console.log('❌ Friend Invitation Test: FAILED');
    console.log('   Error:', error.message);
    return { success: false, error: error.message };
  }
}

/**
 * Test feedback notification email
 * @param {string} testEmail - Email address to send test email to
 * @returns {Promise<Object>} Test result
 */
export async function testFeedbackNotificationEmail(testEmail) {
  console.log('📧 Testing Feedback Notification Email...');
  
  // Verify authentication first
  const isAuthenticated = await verifyAuthentication();
  if (!isAuthenticated) {
    const error = 'Auth session missing! Please log in to the app first.';
    console.log('❌ Feedback Notification Test: FAILED');
    console.log('   Error:', error);
    return { 
      success: false, 
      error,
      diagnostic: 'You must be logged in to test email functionality.'
    };
  }
  
  try {
    const response = await SendEmail({
      to: testEmail,
      template: 'feedback-notification',
      data: {
        feedback: {
          feedback_type: 'bug',
          priority: 'high',
          user_email: testEmail,
          description: 'This is a test feedback notification',
          created_at: new Date().toISOString()
        },
        adminUrl: window.location.origin + '/admin/feedback',
        timestamp: new Date().toLocaleString()
      }
    });
    
    if (response && response.success) {
      console.log('✅ Feedback Notification Test: PASSED');
      console.log('   Message ID:', response.messageId);
      return { success: true, messageId: response.messageId };
    } else {
      console.log('❌ Feedback Notification Test: FAILED');
      return { success: false, error: 'No success response', response };
    }
  } catch (error) {
    console.log('❌ Feedback Notification Test: FAILED');
    console.log('   Error:', error.message);
    return { success: false, error: error.message };
  }
}

/**
 * Test feedback confirmation email
 * @param {string} testEmail - Email address to send test email to
 * @returns {Promise<Object>} Test result
 */
export async function testFeedbackConfirmationEmail(testEmail) {
  console.log('📧 Testing Feedback Confirmation Email...');
  
  // Verify authentication first
  const isAuthenticated = await verifyAuthentication();
  if (!isAuthenticated) {
    const error = 'Auth session missing! Please log in to the app first.';
    console.log('❌ Feedback Confirmation Test: FAILED');
    console.log('   Error:', error);
    return { 
      success: false, 
      error,
      diagnostic: 'You must be logged in to test email functionality.'
    };
  }
  
  try {
    const response = await SendEmail({
      to: testEmail,
      template: 'feedback-confirmation',
      data: {
        feedback: {
          feedback_type: 'feature_request',
          description: 'This is a test feedback confirmation',
          created_at: new Date().toISOString()
        },
        userEmail: testEmail,
        timestamp: new Date().toLocaleString()
      }
    });
    
    if (response && response.success) {
      console.log('✅ Feedback Confirmation Test: PASSED');
      console.log('   Message ID:', response.messageId);
      return { success: true, messageId: response.messageId };
    } else {
      console.log('❌ Feedback Confirmation Test: FAILED');
      return { success: false, error: 'No success response', response };
    }
  } catch (error) {
    console.log('❌ Feedback Confirmation Test: FAILED');
    console.log('   Error:', error.message);
    return { success: false, error: error.message };
  }
}

/**
 * Test password reset email (Supabase Auth)
 * @param {string} testEmail - Email address to send test email to
 * @returns {Promise<Object>} Test result
 */
export async function testPasswordResetEmail(testEmail) {
  console.log('📧 Testing Password Reset Email (Supabase Auth)...');
  
  try {
    // First, check if the email exists in the system
    console.log('   Checking if email exists in Supabase Auth...');
    
    const { error } = await supabase.auth.resetPasswordForEmail(testEmail, {
      redirectTo: window.location.origin + '/auth/reset-password'
    });
    
    if (error) {
      console.log('❌ Password Reset Test: FAILED');
      console.log('   Error:', error.message);
      console.log('   Full error:', error);
      console.log('   Error status:', error.status);
      console.log('   Error name:', error.name);
      
      let diagnostic = '';
      if (error.message.includes('SMTP') || error.message.includes('smtp')) {
        diagnostic = 'SMTP configuration issue. CRITICAL: Verify the Password field in Supabase Auth SMTP settings contains your Resend API key exactly: re_DejfruYV_BgYkMVp6pHkW8RgQAoGnEtRm. Then click "Test SMTP" button to verify connection works.';
      } else if (error.message.includes('rate limit') || error.message.includes('too many')) {
        diagnostic = 'Rate limit exceeded. Wait a few minutes and try again. Check "Minimum interval per user" setting in Supabase Auth SMTP settings.';
      } else if (error.message.includes('email') || error.message.includes('user') || error.message.includes('not found')) {
        diagnostic = 'Email may not be registered, BUT since you confirmed accounts are active, this is likely an SMTP configuration issue. Check: 1) SMTP password is set correctly, 2) Test SMTP connection, 3) Domain verification in Resend.';
      } else if (error.message.includes('recovery') || error.message.includes('sending')) {
        diagnostic = 'Password reset email failed to send. Check Supabase Auth logs for the exact error. Common causes: 1) Domain not verified in Resend (most common), 2) SMTP password not set correctly, 3) Domain mismatch between sender email and verified domain.';
      } else if (error.message.includes('domain') || error.message.includes('not verified') || error.message.includes('450')) {
        diagnostic = 'DOMAIN VERIFICATION ISSUE: The domain in your sender email is not verified in Resend. Go to Resend Dashboard → Domains, add your domain, and add the DNS records (SPF, DKIM) that Resend provides. Wait for DNS propagation (up to 48 hours).';
      } else {
        diagnostic = `SMTP or configuration issue. Error: ${error.message}. Check: 1) Supabase Auth SMTP settings, 2) Resend dashboard logs, 3) Supabase Auth logs. Run window.supabaseAuthEmailTest.testPasswordResetWithDiagnostics("${testEmail}") for detailed diagnostics.`;
      }
      
      if (diagnostic) {
        console.log('   ⚠️  Diagnostic:', diagnostic);
      }
      
      return { 
        success: false, 
        error: error.message,
        errorStatus: error.status,
        errorName: error.name,
        diagnostic 
      };
    } else {
      console.log('✅ Password Reset Test: Request accepted by Supabase');
      console.log('   Note: Check your email inbox. If no email arrives, this indicates an SMTP configuration issue.');
      console.log('   If email doesn\'t arrive, verify: 1) SMTP password is set correctly, 2) Test SMTP connection, 3) Check Resend logs');
      return { 
        success: true,
        note: 'Request accepted. If email doesn\'t arrive, check SMTP configuration.'
      };
    }
  } catch (error) {
    console.log('❌ Password Reset Test: FAILED');
    console.log('   Error:', error.message);
    return { success: false, error: error.message };
  }
}

/**
 * Run all email tests
 * @param {string} testEmail - Email address to send test emails to
 * @returns {Promise<Object>} Test results summary
 */
export async function runAllEmailTests(testEmail) {
  console.log('🧪 Starting All Email Functionality Tests...\n');
  
  if (!testEmail) {
    console.error('❌ Please provide a test email address');
    return { error: 'Test email required' };
  }

  const results = {
    edgeFunction: await testEdgeFunction(testEmail),
    friendInvitation: await testFriendInvitationEmail(testEmail),
    feedbackNotification: await testFeedbackNotificationEmail(testEmail),
    feedbackConfirmation: await testFeedbackConfirmationEmail(testEmail),
    passwordReset: await testPasswordResetEmail(testEmail)
  };

  // Summary
  console.log('\n📊 Test Summary:');
  console.log('================');
  console.log('Edge Function:', results.edgeFunction?.success ? '✅ PASSED' : '❌ FAILED');
  console.log('Friend Invitation:', results.friendInvitation?.success ? '✅ PASSED' : '❌ FAILED');
  console.log('Feedback Notification:', results.feedbackNotification?.success ? '✅ PASSED' : '❌ FAILED');
  console.log('Feedback Confirmation:', results.feedbackConfirmation?.success ? '✅ PASSED' : '❌ FAILED');
  console.log('Password Reset:', results.passwordReset?.success ? '✅ PASSED' : '❌ FAILED');
  
  const passed = Object.values(results).filter(r => r?.success).length;
  const total = Object.keys(results).length;
  
  console.log(`\n🎯 Results: ${passed}/${total} tests passed`);
  
  if (passed === total) {
    console.log('🎉 All email tests passed! Check your inbox.');
  } else {
    console.log('⚠️  Some tests failed. Check the errors above and verify your configuration.');
  }
  
  return results;
}

// Make functions available globally for browser console testing
if (typeof window !== 'undefined') {
  window.emailTest = {
    testEdgeFunction,
    testFriendInvitationEmail,
    testFeedbackNotificationEmail,
    testFeedbackConfirmationEmail,
    testPasswordResetEmail,
    runAllEmailTests
  };
  
  console.log('📧 Email testing utilities loaded!');
  console.log('   Usage: window.emailTest.runAllEmailTests("your-email@example.com")');
  console.log('   Or test individually: window.emailTest.testEdgeFunction("your-email@example.com")');
}

